import React, { useState, useEffect } from 'react';
import CandidateList from '../components/CandidateList';
import useApi from '../hooks/useApi';

/**
 * Candidates Page
 * 
 * This page displays a list of candidates with filtering options.
 */
const CandidatesPage = () => {
  const { data: apiData, loading, error } = useApi('candidates');
  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  
  // Update candidates state when API data is received
  useEffect(() => {
    if (apiData && apiData.candidates) {

      setCandidates(apiData.candidates);
    } else if (!loading) {

      setCandidates([]);
    }
  }, [apiData, loading]);
  
  // Available skills for filtering
  const availableSkills = [
    'JavaScript', 'React', 'Node.js', 'Python', 'Django', 
    'MongoDB', 'PostgreSQL', 'CSS', 'HTML5', 'Docker'
  ];

  const handleSkillToggle = (skill) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Candidate Search
          </h1>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500">
            Find the perfect candidate for your job openings
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="mb-6">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search Candidates
            </label>
            <input
              type="text"
              id="search"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Search by name, skills, or location"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Filter by Skills</h3>
            <div className="flex flex-wrap gap-2">
              {availableSkills.map((skill) => (
                <button
                  key={skill}
                  onClick={() => handleSkillToggle(skill)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedSkills.includes(skill)
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Candidates List */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Candidates</h2>
          {loading ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="text-gray-500">Loading candidates...</div>
            </div>
          ) : (
            <CandidateList candidates={candidates} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidatesPage;
